import { useMemo } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { Tables, hashCode, SummaryTableDepths } from "@repo/utils";
import { ELEMENT_TYPES } from "@utils";
import {
  useEstimateStructure,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  useTableExpandHandler,
  useEstimateTableSettings,
  Row,
  RowGroup,
} from "@tools";
import { Collapse } from "@material-ui/core";
import { ColumnFactory } from "./ColumnFactory";
import { SummaryNewChildRow } from "./SummaryNewChildRow";
import { TitleCell } from "./cells";
import { ExpandContainer } from "../../../cells";
import { SummaryRowGroupList } from "../SummaryRowGroupList";

export const SummaryRowGroup = observer(({ workType }) => {
  const structure = useEstimateStructure();
  const { useExpandCell } = useEstimateTableSettings();
  const {
    isProposal,
    isSellerOrClient,
    allowEdition,
    useVisibilityCell,
    blockExpansions,
    forceExpansionArrows,
  } = useEstimateEditorSettings();

  const { hasTeamMembersCol } = structure.settings;

  const {
    id,
    isOpened,
    treePath,
    resources,
    displayResources,
    turnOff,
    hasFixedPrice,
    isHidden,
  } = workType;
  
  const className = useVisibilityModeCellClassName(isHidden);
  
  const visibleResources = useMemo(() => (
    isProposal
      ? displayResources
      : resources || []
  ), [isProposal, displayResources, resources]);
  
  const expandable = useMemo(() => (
   allowEdition || visibleResources.length
  ), [hasTeamMembersCol, allowEdition, visibleResources]);
  
  const expanded = useMemo(() => (
    expandable && (isOpened || (blockExpansions && !forceExpansionArrows))
  ), [hasTeamMembersCol, expandable, isOpened, blockExpansions]);

  const displayCellContent = useMemo(() => !(
    expanded && visibleResources.length
  ), [expanded, visibleResources]);

  const displayClientCellContent = useMemo(() => (
    !hasFixedPrice && isSellerOrClient && displayCellContent
  ), [hasFixedPrice, isSellerOrClient, displayCellContent]);
  
  const hashedPath = useMemo(() => (
    [Tables.SUMMARY, ...treePath].map((id) => hashCode(id))
  ), [treePath]);

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: expandable,
    isOpened: workType.isOpened,
    onExpand: workType.setOpenState
  });

  const handleTurnOffState = () => {
    workType.setTurnOffState(!turnOff);
  };

  const handleResourceReorder = (list) => workType.reorderResources(list);

  const tableDepth = SummaryTableDepths.WORK_TYPE;

  return (
    <RowGroup
      tableDepth={tableDepth}
      isActive={expanded}
      isStatic={!expandable && !forceExpansionArrows}
      hashedPath={hashedPath}
      elementType={ELEMENT_TYPES.WORK}
      presetClass="preset-accentSummary"
      className={className}
    >
      <Row
        useDragHandleCell={allowEdition}
        useVisibilityCell={useVisibilityCell}
        expandOnClick
        expandable={expandable}
        expanded={expanded}
        onExpand={handleOpen}
        visible={!turnOff}
        onVisibilityToggle={handleTurnOffState}
        isFirstGroupRow
        isLastGroupRow={!expanded}
        tableDepth={tableDepth}
        presetClass={expanded ? "preset-activeSummary" : "preset-rowSummary"}
        actionPresetClass="preset-paper"
      >
        <TitleCell workType={workType}>
          {
            useExpandCell &&
            <ExpandContainer
              tableDepth={tableDepth}
              expandable={expandable && !forceExpansionArrows}
              expanded={expanded}
              noArrow
              // onExpand={handleOpen}
            />
          }
        </TitleCell>
        <ColumnFactory
          element={workType}
          displayCellContent={displayCellContent}
          displayClientCellContent={displayClientCellContent}
        />
      </Row>
      <Collapse
        in={expanded}
        className="w-full"
        mountOnEnter
        unmountOnExit
        data-id={hashCode(`${id}_cont`)}
        data-p={hashedPath.join("/")}
        data-t={ELEMENT_TYPES.RESOURCE}
        { ...collapseProps }
      >
        <SummaryRowGroupList
          listElements={visibleResources}
          isLastGroupRow={!allowEdition}
          onReorder={handleResourceReorder}
          tableDepth={tableDepth}
          turnOff={turnOff}
          disabled={!allowEdition}
          hashedPath={hashedPath}
        />
        {
          allowEdition &&
          <SummaryNewChildRow workType={workType} />
        }
      </Collapse>
    </RowGroup>
  );
});

SummaryRowGroup.propTypes = {
  workType: object.isRequired,
};
